<template>
    <div class="flex flex-column xl:flex-row xl:align-items-center justify-content-between w-full gap-3">
        <div class="flex align-items-center">
            <Inplace
                ref="inplace"
                :pt="{
                root: {class: 'align-self-start'},
                display: {class: 'inline-block', style: 'margin-left: -12px;'}
            }"
            >
                <template #display>
                    <div class="flex align-items-center gap-2">
                        <h3 :title="getName">{{ getName }}</h3>
                        <i class="pi pi-pencil cluster_action__icon"></i>
                    </div>
                </template>
                <template #content>
                    <div class="flex align-items-center gap-2" :class="{'p-disabled': this.loading}">
                        <InputText
                            v-model="model.name"
                            @keyup.enter="onRenameProject"
                            @keyup.esc="onCloseRename"
                            @focusout="onCloseRename"
                            autofocus
                            class="cluster_action__input"
                        />
                        <i class="pi pi-pencil cluster_action__icon"></i>
                    </div>
                </template>
            </Inplace>
            <KSClusterHistory :uid="uid"></KSClusterHistory>
        </div>
        <div class="flex-wrap flex flex-column sm:flex-row gap-2 w-full sm:w-auto">
            <KSUpdateFrequency :is-demo="isDemo" />
            <KSAutoCluster :is-demo="isDemo" :monitoring="this.monitoring" />
            <Button
                :label="$t('lang.cluster.add_keys')"
                size="small"
                icon="pi pi-angle-down"
                icon-pos="right"
                @click="toggleAdd"
                id="tour-step-10"
                :pt="{
                    icon: { class: 'absolute sm:static', style: 'right: 1rem' }
                }"
            />
            <Button
                :label="$t('lang.cluster.remove_keys')"
                size="small"
                icon="pi pi-angle-down"
                icon-pos="right"
                @click="toggleDelete"
                id="tour-step-11"
                :disabled="getSelectNode.id === -2"
                :pt="{
                    icon: { class: 'absolute sm:static', style: 'right: 1rem' }
                }"
            />
            <KSExportCluster />

            <TieredMenu ref="tieredMenuAdd" :model="tieredMenu.add" popup />
            <TieredMenu ref="tieredMenuDelete" :model="tieredMenu.delete" popup />
        </div>
        <KSFromTextArea :uid="uid" :type="'import'" :name="getListenerImportFromAreaOpenId"></KSFromTextArea>
        <KSFromFile :uid="uid" :type="'import'" :name="getListenerImportFromFileOpenId" :is-demo="isDemo"></KSFromFile>
        <KSFromGroupReport :uid="uid" :base="base" :name="getListenerImportGroupReportOpenId"></KSFromGroupReport>

        <KSFromTextArea :uid="uid" :type="'delete'" :name="getListenerDeleteFromAreaOpenId"></KSFromTextArea>
        <KSFromFile :uid="uid" :type="'delete'" :name="getListenerDeleteFromFileOpenId"></KSFromFile>
        <KSFromFilter :uid="uid" :type="'delete'" :name="getListenerDeleteFromFilterOpenId"></KSFromFilter>
        <KSFromFilterGroup :uid="uid" :type="'delete'" :name="getListenerDeleteFromFilterGroupOpenId"></KSFromFilterGroup>
        <KSDeleteDoubles :uid="uid" :name="getListenerDeleteDoublesOpenId"></KSDeleteDoubles>
        <KSFromTemplate :uid="uid" :type="'delete'" :name="getListenerDeleteFromTemplateOpenId"></KSFromTemplate>
    </div>
</template>

<script>
import KSAutoCluster from './KSAutoCluster.vue'
import KSUpdateFrequency from './KSUpdateFrequency.vue'
import KSExportCluster from './KSExportCluster.vue'
import KSFromFile from './KSFromFile.vue'
import KSFromTextArea from './KSFromTextArea.vue'
import KSFromGroupReport from './KSFromGroupReport.vue'
import KSFromFilter from './KSFromFilter.vue'
import KSFromFilterGroup from './KSFromFilterGroup.vue'
import KSDeleteDoubles from './KSDeleteDoubles.vue'
import KSFromTemplate from './KSFromTemplate.vue'
import {Listeners} from "../../../constants/listeners";
import {store} from "../store";
import KSSelect from "../../../component/KSSelect.vue";
import KSClusterTableActions from '../component/KSClusterTableActions.vue';
import KSClusterHistory from "./KSClusterHistory.vue";
import TieredMenu from "primevue/tieredmenu";
import Button from "primevue/button";
import Inplace from "primevue/inplace";
import InputText from "primevue/inputtext";
import ClusterApi from "../../../api/ClusterApi";
import Alerts from "../../../mixins/Alerts";

export default {
    mixins: [Alerts],
    props: {
        uid: {
            type: String,
            required: true,
        },
        projectName: {
            type: String,
            required: true,
        },
        base: {
            type: String,
            required: true,
        },
        isDemo: {
            type: Boolean,
            required: false,
            default: false,
        },
        monitoring: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            model: {
                name: this.projectName,
            },
            currentName: this.projectName,
            loading: false,
            store: store,
            tieredMenu: {
                add:  [
                    {
                        label: this.$t('lang.cluster.cls_from_area_1'),
                        command: () => this.showComponent(this.getListenerImportFromAreaOpenId),
                        availableOnDemo: true,
                    },
                    {
                        label: this.$t('lang.cluster.cls_from_file'),
                        command: () => this.showComponent(this.getListenerImportFromFileOpenId),
                        availableOnDemo: true,
                    },
                    {
                        label: this.$t('lang.cluster.cls_from_grp_report'),
                        command: () => this.showComponent(this.getListenerImportGroupReportOpenId),
                        availableOnDemo: false,
                    },
                ],
                delete: [
                    {
                        label: this.$t('lang.cluster.cls_from_area'),
                        command: () => this.showComponent(this.getListenerDeleteFromAreaOpenId)
                    },{
                        label: this.$t('lang.cluster.cls_from_file'),
                        command: () => this.showComponent(this.getListenerDeleteFromFileOpenId)
                    },{
                        label: this.$t('lang.cluster.cls_from_filter'),
                        command: () => this.showComponent(this.getListenerDeleteFromFilterOpenId)
                    },{
                        label: this.$t('lang.cluster.cls_from_flt_grp'),
                        command: () => this.showComponent(this.getListenerDeleteFromFilterGroupOpenId)
                    },{
                        label: this.$t('lang.cluster.cls_from_double'),
                        command: () => this.showComponent(this.getListenerDeleteDoublesOpenId)
                    },{
                        label: this.$t('lang.cluster.cls_from_tmpl'),
                        command: () => this.showComponent(this.getListenerDeleteFromTemplateOpenId)
                    },
                ]
            }
        }
    },
    created() {
        if (this.isDemo) {
            this.tieredMenu.add = this.tieredMenu.add.filter((item) => {
                return item.availableOnDemo === true
            })
        }
    },
    mounted() {
        this.$emitter.on(Listeners.CLUSTER_SHOW_COMPONENT, eventData => this.onShowComponent(eventData));
    },
    computed: {
        getName() {
            return this.currentName;
        },
        getSelectNode() {
            return this.store.getters.selectNode();
        },
        getListenerImportFromAreaOpenId() {
            return Listeners.CLUSTER_POPUP_IMPORT_FROM_AREA_OPEN;
        },
        getListenerImportFromFileOpenId() {
            return Listeners.CLUSTER_POPUP_IMPORT_FROM_FILE_OPEN;
        },
        getListenerImportGroupReportOpenId() {
            return Listeners.CLUSTER_POPUP_IMPORT_GROUP_REPORT_OPEN;
        },
        getListenerDeleteFromAreaOpenId() {
            return Listeners.CLUSTER_POPUP_DELETE_FROM_AREA_OPEN;
        },
        getListenerDeleteFromFileOpenId() {
            return Listeners.CLUSTER_POPUP_DELETE_FROM_FILE_OPEN;
        },
        getListenerDeleteFromFilterOpenId() {
            return Listeners.CLUSTER_POPUP_DELETE_FROM_FILTER_OPEN;
        },
        getListenerDeleteFromFilterGroupOpenId() {
            return Listeners.CLUSTER_POPUP_DELETE_FROM_FILTER_GROUP_OPEN;
        },
        getListenerDeleteDoublesOpenId() {
            return Listeners.CLUSTER_POPUP_DELETE_DOUBLES_OPEN
        },
        getListenerDeleteFromTemplateOpenId() {
            return Listeners.CLUSTER_POPUP_DELETE_FROM_TEMPLATE_OPEN
        },
    },
    methods: {
        onRenameProject() {
            this.loading = true;
            ClusterApi
                .renameProject(this.uid, this.model)
                .then(() => {
                    this.currentName = this.model.name;
                    this.$refs.inplace.close();
                })
                .catch(error => this.alertError(error))
                .finally(() => this.loading = false);
        },
        onCloseRename() {
            this.$refs.inplace.close();
            this.model.name = this.currentName;
        },
        showComponent(dialogListenerId) {
            this.$emitter.emit(dialogListenerId)
        },
        onShowComponent(event) {
            this.showComponent(event.component);
        },
        toggleAdd(event) {
            this.$refs.tieredMenuAdd.toggle(event);
        },
        toggleDelete(event) {
            this.$refs.tieredMenuDelete.toggle(event);
        },
    },
    components: {
        KSAutoCluster,
        KSUpdateFrequency,
        KSExportCluster,
        KSSelect,
        KSFromFile,
        KSFromTextArea,
        KSFromGroupReport,
        KSFromFilter,
        KSFromFilterGroup,
        KSDeleteDoubles,
        KSFromTemplate,
        KSClusterTableActions,
        TieredMenu,
        Button,
        Inplace,
        InputText,
        KSClusterHistory,
    },
}
</script>

<style scoped>
.cluster_action__input {
    font-size: 24px;
    margin: 1px 0 0;
}
.cluster_action__icon {
    color: var(--secondary-color);
}
</style>
